import moment from "moment-timezone";

/**
 * Relative time ("time ago") formatting utilities
 * Converts a timestamp into a human-readable string like
 * "just now", "5 seconds ago", "3 hours ago", "2 weeks ago", or "in 4 days".
 */

const UNIT_MS = {
  year: 365 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
  hour: 60 * 60 * 1000,
  minute: 60 * 1000,
  second: 1000,
};

const UNIT_ORDER = ["year", "month", "week", "day", "hour", "minute", "second"];

const pluralize = (value, unit) => `${value} ${unit}${value === 1 ? "" : "s"}`;

/**
 * Decompose a timestamp into its relative time parts (largest unit first).
 * @param {string|Date} date - The date to compare against now
 * @param {moment.Moment} [now=moment()] - Reference "now" (injectable for tests)
 * @returns {{ direction: "past"|"future", parts: Array<{unit: string, value: number}>, largest: {unit: string, value: number}|null } | null}
 *          null if the date is invalid
 */
export const getTimeAgoParts = (date, now = moment()) => {
  if (!date) return null;
  const target = moment(date);
  if (!target.isValid()) return null;

  const diffMs = now.diff(target);
  let remainder = Math.abs(diffMs);

  const parts = [];
  for (const unit of UNIT_ORDER) {
    const ms = UNIT_MS[unit];
    if (remainder >= ms) {
      const value = Math.floor(remainder / ms);
      parts.push({ unit, value });
      remainder -= value * ms;
    }
  }

  return {
    direction: diffMs >= 0 ? "past" : "future",
    parts,
    largest: parts[0] || null,
  };
};

/**
 * Format a timestamp as a relative time string.
 *
 * @param {string|Date} date - The date to format
 * @param {Object} [options]
 * @param {moment.Moment} [options.now=moment()] - Reference "now" (injectable for tests)
 * @param {boolean} [options.detailed=false] - Include all units ("2 hours 15 minutes ago" vs "2 hours ago")
 * @param {boolean} [options.showAgo=true] - Append "ago" / "in" wording ("2 hours ago" vs "2 hours")
 * @param {boolean} [options.future=true] - Render future dates as "in 2 hours"; false returns "" for future
 * @param {number} [options.justNowThresholdMs=5000] - Ages under this threshold render as "just now"
 * @param {number} [options.fallbackAfterMs=null] - Past ages at/above this are rendered as an absolute
 *                                                 date instead (e.g. "18 Aug 2026")
 * @returns {string} Relative time string, or "" for missing/invalid input
 */
export const formatTimeAgo = (date, options = {}) => {
  const {
    now = moment(),
    detailed = false,
    showAgo = true,
    future = true,
    justNowThresholdMs = 5000,
    fallbackAfterMs = null,
  } = options;

  if (!date) return "";
  const result = getTimeAgoParts(date, now);
  if (!result) return "";

  if (result.direction === "future") {
    if (!future) return "";
    if (!result.largest || result.largest.unit === "second") return "just now";
    const body = detailed
      ? result.parts.map(p => pluralize(p.value, p.unit)).join(", ")
      : pluralize(result.largest.value, result.largest.unit);
    return showAgo ? `in ${body}` : body;
  }

  if (justNowThresholdMs > 0 && result.largest && result.largest.unit === "second") {
    const ageMs = now.diff(moment(date));
    if (ageMs < justNowThresholdMs) return "just now";
  }

  if (fallbackAfterMs != null && result.largest) {
    const ageMs = now.diff(moment(date));
    if (ageMs >= fallbackAfterMs) {
      return moment(date).format("DD MMM YYYY");
    }
  }

  const body = detailed
    ? result.parts.map(p => pluralize(p.value, p.unit)).join(", ")
    : pluralize(result.largest.value, result.largest.unit);

  return showAgo ? `${body} ago` : body;
};

/**
 * Compact relative time for badges/chips: "just now", "5s ago", "3h ago", "2d ago", "4w ago", "2mo ago", "1y ago".
 *
 * @param {string|Date} date - The date to format
 * @param {Object} [options] - Same options as formatTimeAgo (justNowThresholdMs, fallbackAfterMs, now)
 * @returns {string} Compact relative time string, or "" for missing/invalid input
 */
export const formatTimeAgoShort = (date, options = {}) => {
  const { now = moment(), justNowThresholdMs = 5000, fallbackAfterMs = null } = options;

  if (!date) return "";
  const result = getTimeAgoParts(date, now);
  if (!result) return "";

  if (result.direction === "future") {
    if (!result.largest || result.largest.unit === "second") return "just now";
    return `in ${result.largest.value}${shortUnit(result.largest.unit)}`;
  }

  if (result.largest && result.largest.unit === "second") {
    const ageMs = now.diff(moment(date));
    if (ageMs < justNowThresholdMs) return "just now";
  }

  if (fallbackAfterMs != null && result.largest) {
    const ageMs = now.diff(moment(date));
    if (ageMs >= fallbackAfterMs) {
      return moment(date).format("DD MMM YYYY");
    }
  }

  return `${result.largest.value}${shortUnit(result.largest.unit)} ago`;
};

const shortUnit = unit => {
  switch (unit) {
    case "year":
      return "y";
    case "month":
      return "mo";
    case "week":
      return "w";
    case "day":
      return "d";
    case "hour":
      return "h";
    case "minute":
      return "m";
    case "second":
      return "s";
    default:
      return "";
  }
};

const timeAgoUtils = {
  getTimeAgoParts,
  formatTimeAgo,
  formatTimeAgoShort,
};

export default timeAgoUtils;
